import React, { useEffect, useRef, useState } from "react";

import CloseIcon from "@mui/icons-material/Close";
import DeleteIcon from "@mui/icons-material/Delete";
import EditIcon from "@mui/icons-material/Edit";
import PublishIcon from "@mui/icons-material/Publish";
import VisibilityIcon from "@mui/icons-material/Visibility";
import ClearIcon from "@mui/icons-material/Clear";
import SaveIcon from "@mui/icons-material/Save";

import Box from "@mui/material/Box";
import Divider from "@mui/material/Divider";
import IconButton from "@mui/material/IconButton";
import MenuItem from "@mui/material/MenuItem";
import MenuList from "@mui/material/MenuList";
import Paper from "@mui/material/Paper";
import Popper from "@mui/material/Popper";
import Tooltip from "@mui/material/Tooltip";
import Typography from "@mui/material/Typography";
import Popover from "@mui/material/Popover";
import FormGroup from "@mui/material/FormGroup";
import FormControlLabel from "@mui/material/FormControlLabel";
import Checkbox from "@mui/material/Checkbox";
import TextField from "@mui/material/TextField";
import Link from "@mui/material/Link";

import {
    getDeleteConstraintButtonVisibility,
    getEditConstraintButtonVisibility,
    getGeometryStyleFromColor,
    getPublishConstraintButtonVisibility,
    getTagButtonVisibility,
    IsMapBeingDrawnOn,
    getFeatureTypeFromFeature,
    getAssociatedFlightButtonTooltipFromFeatureAndOperations,
    getAssociatedFlightButtonIconFromFeature,
    getZcButtonTooltipFromFeatureAndZcConfigurations,
    getZcButtonIconFromFeatureAndUser,
    getZcButtonCheckedFromFeatureAndZcConfigurations,
    getAssociatedFlightButtonCheckedFromFeatureAndFlightUuid,
    getFeatureInfoFromFeature,
    isFeatureSelectable,
    getFeatureTitleFromFeature,
    hasTaggedCallsign
} from "../map/mapUtil";

import { useUserAuth } from "../contexts/authContext";
import { useMap } from "../contexts/mapContext";

export default function OpenLayersInfoCard({
    map,
    showButtons,
    featureToShow,
    handleWatchEntityButtonClick,
    handleEditConstraintButtonClick,
    airspaceSubLayers,
    mapContainer,
    clearFeature,
    handleShowEntityDpz,
    featureToRotateRef
}) {
    const [popperPosition, setPopperPosition] = useState(null);
    const [featuresClicked, setFeaturesClicked] = useState([]);
    const [featureInfo, setFeatureInfo] = useState([]);
    const [featureDeleted, setFeatureDeleted] = useState(false);

    const [editingCallsign, setEditingCallsign] = useState(false);
    const [callsignInput, setCallsignInput] = useState("");

    const [flightPopoverAnchorEl, setFlightPopoverAnchorEl] = useState(null);
    const flightPopoverOpen = Boolean(flightPopoverAnchorEl);

    const [allZcConfigurations, setAllZcConfigurations] = useState([]);
    const [zcPopoverAnchorEl, setZcPopoverAnchorEl] = useState(null);
    const zcPopoverOpen = Boolean(zcPopoverAnchorEl);

    const featuresClickedRef = useRef([]);
    const selectedSensorRef = useRef(null);

    const { user, setSnackbar, handleFailedFetch } = useUserAuth();
    const { publishedOperations } = useMap();

    useEffect(() => {
        handleFetchZeroConflictConfigurations();
    }, []);

    useEffect(() => {
        if (!map) {
            return;
        }

        map.on("click", handleMapSelection);

        return () => {
            if (map) {
                map.un("click", handleMapSelection);
            }
        };
    }, [map, airspaceSubLayers, featuresClicked]);

    useEffect(() => {
        if (!map) {
            return;
        }

        map.on("pointermove", setupDynamicCursor);

        return () => {
            if (map) {
                map.un("pointermove", setupDynamicCursor);
            }
        };
    }, [map, airspaceSubLayers]);

    useEffect(() => {
        if (!map) {
            return;
        }

        map.getLayers().on("add", handleFeatureChanges);

        return () => {
            if (map) {
                map.getLayers().un("add", handleFeatureChanges);
            }
        };
    }, [map]);

    useEffect(() => {
        if (selectedSensorRef.current) {
            const style = getGeometryStyleFromColor("#000000");
            selectedSensorRef.current.setStyle(style);
            selectedSensorRef.current = null;
        }
        featuresClickedRef.current = featuresClicked;

        // We will only get the info of a feature if there is only 1 selected.
        if (featuresClicked.length !== 1) {
            setFeatureDeleted(false);
            return;
        }

        // Set the feature to rotate.
        if (featureToRotateRef) {
            featureToRotateRef.current = featuresClicked[0];
        }

        // Get the entity's info to display in the info card.
        const featureInfo = getFeatureInfoFromFeature(featuresClicked[0]);
        setFeatureInfo(featureInfo);

        const isSensor = featuresClicked[0].get("sensor");
        if (isSensor) {
            const style = getGeometryStyleFromColor("#00FF00");
            featuresClicked[0].setStyle(style);
            selectedSensorRef.current = featuresClicked[0];
        }
    }, [featuresClicked]);

    useEffect(() => {
        if (featureToShow) {
            setFeaturesClicked([featureToShow]);
        }
    }, [featureToShow]);

    useEffect(() => {
        if (clearFeature) {
            setFeaturesClicked([]);
        }
    }, [clearFeature]);

    const handleMapSelection = ({ pixel, originalEvent }) => {
        // don't do anything if actively drawing on the map
        if (IsMapBeingDrawnOn(map)) {
            return false;
        }
        // if there are features already selected, reset and return
        if (Array.isArray(featuresClicked) && featuresClicked.length > 1) {
            setFeaturesClicked([]);
            return false;
        }
        // otherwise continue
        const features = [];
        map.forEachFeatureAtPixel(pixel, (feature, layer) => {
            const selectable = isFeatureSelectable(feature, layer, airspaceSubLayers);
            if (selectable === false) return;
            features.push(feature);
        });
        if (features.length === 0) {
            setFeatureInfo([]);
            setFeaturesClicked(features);
        } else if (features.length === 1) {
            handleSelectFeature(features[0]);
        } else {
            setFeaturesClicked(features);

            const virtualElement = {
                getBoundingClientRect: () => ({
                    width: 0,
                    height: 0,
                    top: originalEvent.y,
                    right: originalEvent.x,
                    bottom: originalEvent.y,
                    left: originalEvent.x
                }),
                nodeType: 1
            };
            setPopperPosition(virtualElement);
        }
        setFeatureDeleted(false);
    };

    // Sets up the cursor changing to pointer when hovering over selectable layers.
    const setupDynamicCursor = (event) => {
        if (IsMapBeingDrawnOn(map)) {
            return;
        }
        let selectableLayer = false;
        map.forEachFeatureAtPixel(event.pixel, (feature, layer) => {
            if (selectableLayer === false) {
                selectableLayer = isFeatureSelectable(feature, layer, airspaceSubLayers);
            }
        });
        map.getTargetElement().style.cursor = selectableLayer ? "pointer" : "";
    };

    // Handles the changes and removals of features whose info is being displayed.
    const handleFeatureChanges = ({ element }) => {
        const layer = element;
        if (!layer.get("selectable")) {
            return;
        }
        const source = layer.getSource();
        /* istanbul ignore next */
        source.on("changefeature", (event) => {
            const changedFeature = event.feature;
            if (featuresClickedRef.current.length === 1 && featuresClickedRef.current[0].getId() === changedFeature.getId()) {
                if (featuresClickedRef.current[0].get("sensor")) {
                    return;
                }
                setFeatureDeleted(false);
                setFeaturesClicked([changedFeature]);
            }
        });
        /* istanbul ignore next */
        source.on("removefeature", (event) => {
            const removedFeature = event.feature;
            if (featuresClickedRef.current.length === 1 && featuresClickedRef.current[0].getId() === removedFeature.getId()) {
                if (removedFeature.get("asd")) {
                    setFeatureDeleted(true);
                } else {
                    handleCloseInfoCard();
                }
            } else if (featuresClickedRef.current.length > 1) {
                setFeaturesClicked((prev) => prev.filter((feature) => feature.getId() !== removedFeature.getId()));
            }

            // Check if we should reset feature to rotate.
            if (featureToRotateRef && featureToRotateRef.current?.getId() === removedFeature.getId()) {
                featureToRotateRef.current = null;
            }
        });
    };

    const handlePublishConstraintButtonClick = (feature) => {
        if (!confirm("Are you sure you want to publish this constraint?")) {
            return;
        }

        const constraint = feature.get("constraint");
        const currTime = Date.now();
        const constraintStartTime = new Date(constraint.time_start).getTime();
        if (currTime > constraintStartTime) {
            alert("The start time of this constraint has passed. Please update the start time before publishing.");
            return;
        }

        constraint.version = parseInt(constraint.version) + 1;
        constraint.state = "ACCEPTED";

        const requestOptions = {
            method: "PUT",
            headers: { "Content-Type": "application/json" },
            body: JSON.stringify(constraint)
        };
        fetch("api/constraint/update", requestOptions)
            .then((response) => (response.ok ? response.json() : Promise.reject(response)))
            .then(() => setSnackbar({ children: "Constraint successfully updated", severity: "success" }))
            .catch((err) => handleFailedFetch(err));
    };

    const handleDeleteConstraintButtonClick = (feature) => {
        if (!confirm("Are you sure you want to delete this constraint?")) {
            return;
        }

        const requestOptions = {
            method: "PUT",
            headers: { "Content-Type": "application/json" },
            body: JSON.stringify(feature.get("constraint"))
        };
        fetch("/api/constraint/delete", requestOptions)
            .then((response) => (response.ok ? response.json() : Promise.reject(response)))
            .then(() => setSnackbar({ children: "Constraint successfully deleted", severity: "success" }))
            .catch((err) => handleFailedFetch(err));
    };

    const handleCloseInfoCard = () => {
        setFeaturesClicked([]);
        setFeatureInfo([]);
    };
    const handleFetchZeroConflictConfigurations = () => {
        const requestOptions = {
            method: "GET",
            headers: { "Content-Type": "application/json" }
        };
        fetch("/api/zca_configs/get", requestOptions)
            .then((response) => (response.ok ? response.json() : Promise.reject(response)))
            .then((data) => {
                setAllZcConfigurations(data);
            })
            .catch((err) => handleFailedFetch(err));
    };
    const handleUpdateAsdTrack = (callsign, flightUUID, configUUID) => {
        const properties = featuresClicked[0].getProperties();
        const updatedAsd = {
            id: featuresClicked[0].getId(),
            tagged_callsign: callsign,
            show_dpz: configUUID ? true : false,
            organization: user.organization_id,
            history_locations: properties.asd.history_locations,
            flight_uuid: flightUUID,
            user_id: user.id,
            alerting_params_uuid: configUUID
        };
        const requestOptions = {
            method: "PUT",
            headers: { "Content-Type": "application/json" },
            body: JSON.stringify(updatedAsd)
        };
        fetch("api/asd/update", requestOptions)
            .then((response) => (response.ok ? response.json() : Promise.reject(response)))
            .then(() => {
                if (editingCallsign) {
                    setEditingCallsign(false);
                }
                if (flightPopoverAnchorEl) {
                    setFlightPopoverAnchorEl(null);
                }
                if (zcPopoverAnchorEl) {
                    setZcPopoverAnchorEl(null);
                }
                if (handleShowEntityDpz) {
                    handleShowEntityDpz(updatedAsd);
                }
                setCallsignInput(callsign);
            })
            .catch((err) => handleFailedFetch(err));
    };
    const handleAssociatedFlightButtonClick = (flightUUID) => {
        const properties = featuresClicked[0].getProperties();
        if ("asd" in properties) {
            if (properties.asd.daa_configs && properties.asd.daa_configs[user.organization_id]) {
                const configUUID = properties.asd.daa_configs[user.organization_id];
                handleUpdateAsdTrack(callsignInput, flightUUID, configUUID);
            } else {
                handleUpdateAsdTrack(callsignInput, flightUUID, "");
            }
        }
    };
    const handleZcConfigurationButtonClick = (configUUID) => {
        const properties = featuresClicked[0].getProperties();
        if ("asd" in properties && properties.asd.flight_uuid) {
            const flightUUID = properties.asd.flight_uuid;
            handleUpdateAsdTrack(callsignInput, flightUUID, configUUID);
        } else {
            handleUpdateAsdTrack(callsignInput, "N/A", configUUID);
        }
    };
    const handleSaveCallsignButtonClick = () => {
        if (!callsignInput) {
            handleClearCallsignButtonClick();
            return;
        }
        const properties = featuresClicked[0].getProperties();
        if ("asd" in properties) {
            let flightUUID = "N/A";
            if (properties.asd.flight_uuid) {
                flightUUID = properties.asd.flight_uuid;
            }
            let configUUID = "";
            if (properties.asd.daa_configs && properties.asd.daa_configs[user.organization_id]) {
                configUUID = properties.asd.daa_configs[user.organization_id];
            }
            handleUpdateAsdTrack(callsignInput, flightUUID, configUUID);
        }
    };
    const handleClearCallsignButtonClick = () => {
        setEditingCallsign(false);

        const previousCallsign = featuresClicked[0].get("name");
        setCallsignInput(previousCallsign);
    };
    const handleSelectFeature = (feature) => {
        setFeaturesClicked([feature]);

        const isAsdFeature = feature.get("asd");
        if (isAsdFeature) {
            const name = feature.get("name");
            setCallsignInput(name);

            setEditingCallsign(false);
        }
    };
    const handleResetCallsignButtonClick = () => {
        const properties = featuresClicked[0].getProperties();
        if ("asd" in properties) {
            let flightUUID = "N/A";
            if (properties.asd.flight_uuid) {
                flightUUID = properties.asd.flight_uuid;
            }
            let configUUID = "";
            if (properties.asd.daa_configs && properties.asd.daa_configs[user.organization_id]) {
                configUUID = properties.asd.daa_configs[user.organization_id];
            }
            handleUpdateAsdTrack(properties.asd.label, flightUUID, configUUID);
        }
    };
    return featuresClicked.length > 1 ? (
        // Shows the feature menu list if multiple features are selected at once.
        <Popper
            open={featuresClicked.length > 1}
            anchorEl={popperPosition}
            placement={"bottom-end"}
            container={mapContainer ? mapContainer : undefined}
            sx={{ zIndex: 10000 }}
        >
            <Paper>
                <MenuList dense sx={{ maxHeight: 200, overflow: "auto" }}>
                    {featuresClicked.map((feature, i) => (
                        <MenuItem key={i} onClick={() => handleSelectFeature(feature)}>
                            {getFeatureTitleFromFeature(feature)}
                        </MenuItem>
                    ))}
                </MenuList>
            </Paper>
        </Popper>
    ) : featuresClicked.length === 1 && Array.isArray(featureInfo) && featureInfo.length > 0 ? (
        <Paper
            elevation={8}
            sx={{
                position: "absolute",
                zIndex: "1000",
                top: "45px",
                right: "10px",
                maxHeight: "calc(100% - 55px)",
                width: { xs: "calc(100% - 20px)", sm: "350px" },
                boxShadow: featureDeleted ? "0 0 4px 2.5px rgba(255, 0, 0, 0.5)" : "0 4px 10px rgba(0, 0, 0, 0.2)",
                p: 1,
                overflowY: "auto"
            }}
        >
            <Box sx={{ display: "flex", alignItems: "center", justifyContent: "flex-start", width: "100%", overflow: "hidden" }}>
                <Box sx={{ flex: 1, mr: "auto", overflow: "auto" }}>
                    {showButtons && getTagButtonVisibility(user, featuresClicked[0]) && !featureDeleted ? (
                        editingCallsign ? (
                            <Box sx={{ display: "flex", flexDirection: "row", my: 1 }}>
                                <TextField
                                    label="Callsign"
                                    size="small"
                                    value={callsignInput}
                                    onChange={(e) => setCallsignInput(e.target.value)}
                                    sx={{ width: "150px" }}
                                />
                                <Tooltip title="Save">
                                    <IconButton size="small" onClick={handleSaveCallsignButtonClick} sx={{ alignSelf: "center" }}>
                                        <SaveIcon fontSize="small" />
                                    </IconButton>
                                </Tooltip>

                                <Tooltip title="Cancel">
                                    <IconButton size="small" onClick={handleClearCallsignButtonClick} sx={{ alignSelf: "center" }}>
                                        <ClearIcon fontSize="small" />
                                    </IconButton>
                                </Tooltip>
                            </Box>
                        ) : (
                            <Box sx={{ display: "flex", flexDirection: "row" }}>
                                <Tooltip title={getFeatureTitleFromFeature(featuresClicked[0])}>
                                    <Typography variant="h6" sx={{ fontStyle: featureDeleted ? "italic" : "normal" }} noWrap>
                                        {getFeatureTitleFromFeature(featuresClicked[0])}
                                    </Typography>
                                </Tooltip>

                                {hasTaggedCallsign(featuresClicked[0], user) ? (
                                    <Tooltip title="Clear callsign">
                                        <IconButton size="small" onClick={() => handleResetCallsignButtonClick()} sx={{ alignSelf: "center" }}>
                                            <ClearIcon fontSize="small" />
                                        </IconButton>
                                    </Tooltip>
                                ) : (
                                    <Tooltip title="Add callsign">
                                        <IconButton size="small" onClick={() => setEditingCallsign(true)} sx={{ alignSelf: "center" }}>
                                            <EditIcon fontSize="small" />
                                        </IconButton>
                                    </Tooltip>
                                )}
                            </Box>
                        )
                    ) : (
                        <Tooltip title={getFeatureTitleFromFeature(featuresClicked[0])}>
                            <Typography variant="h6" sx={{ fontStyle: featureDeleted ? "italic" : "normal" }} noWrap>
                                {getFeatureTitleFromFeature(featuresClicked[0])}
                            </Typography>
                        </Tooltip>
                    )}
                    <Typography variant="body2" noWrap color={"text.secondary"} fontStyle={featureDeleted ? "italic" : "normal"}>
                        {getFeatureTypeFromFeature(featuresClicked[0])}
                    </Typography>
                </Box>
                {[
                    {
                        title: "Open Watch Toolbar",
                        icon: <VisibilityIcon fontSize="small" />,
                        visible: showButtons && "asd" in featuresClicked[0].getProperties() && !featureDeleted,
                        onClick: () => handleWatchEntityButtonClick(featuresClicked[0].getId())
                    },
                    {
                        title: getAssociatedFlightButtonTooltipFromFeatureAndOperations(featuresClicked[0], publishedOperations),
                        icon: getAssociatedFlightButtonIconFromFeature(featuresClicked[0]),
                        visible: showButtons && getTagButtonVisibility(user, featuresClicked[0]) && !featureDeleted,
                        onClick: (e) => setFlightPopoverAnchorEl(e.currentTarget)
                    },
                    {
                        title: getZcButtonTooltipFromFeatureAndZcConfigurations(featuresClicked[0], allZcConfigurations, user),
                        icon: getZcButtonIconFromFeatureAndUser(featuresClicked[0], user),
                        visible: showButtons && getTagButtonVisibility(user, featuresClicked[0]) && !featureDeleted,
                        onClick: (e) => setZcPopoverAnchorEl(e.currentTarget)
                    },
                    {
                        title: "Publish Constraint",
                        icon: <PublishIcon fontSize="small" />,
                        visible: showButtons && getPublishConstraintButtonVisibility(user, featuresClicked[0]) && !featureDeleted,
                        onClick: () => handlePublishConstraintButtonClick(featuresClicked[0])
                    },
                    {
                        title: "Edit Constraint",
                        icon: <EditIcon fontSize="small" />,
                        visible: showButtons && getEditConstraintButtonVisibility(user, featuresClicked[0]) && !featureDeleted,
                        onClick: () => handleEditConstraintButtonClick(featuresClicked[0])
                    },
                    {
                        title: "Delete Constraint",
                        icon: <DeleteIcon fontSize="small" />,
                        visible: showButtons && getDeleteConstraintButtonVisibility(user, featuresClicked[0]) && !featureDeleted,
                        onClick: () => handleDeleteConstraintButtonClick(featuresClicked[0])
                    },
                    {
                        title: "Close",
                        icon: <CloseIcon fontSize="small" />,
                        visible: true,
                        onClick: () => handleCloseInfoCard()
                    }
                ].map(({ title, visible, icon, onClick }, i) =>
                    visible === true ? (
                        <React.Fragment key={i}>
                            <Tooltip key={i} title={title}>
                                <IconButton size="small" aria-label="Monitor" onClick={onClick}>
                                    {icon}
                                </IconButton>
                            </Tooltip>
                        </React.Fragment>
                    ) : (
                        <React.Fragment key={i} />
                    )
                )}
            </Box>

            <Divider sx={{ my: 1 }} />

            <Box sx={{ display: "flex", flexDirection: "column", gap: 1 }}>
                {featureInfo.map(({ title, value, color, link }, i) => (
                    <Box
                        key={i}
                        sx={{
                            display: "flex",
                            flexDirection: getFeatureTypeFromFeature(featuresClicked[0]) === "Stadium" ? "column" : "row",
                            justifyContent: "space-between",
                            gap: getFeatureTypeFromFeature(featuresClicked[0]) === "Stadium" ? 0 : 1
                        }}
                    >
                        <Typography
                            variant="body2"
                            fontWeight={"bold"}
                            fontStyle={featureDeleted ? "italic" : "normal"}
                            sx={{ whiteSpace: "nowrap", color: color || "text.secondary" }}
                        >
                            {title}
                        </Typography>

                        {value ? (
                            <Typography variant="body2" sx={{ color: color || "text.secondary" }} fontStyle={featureDeleted ? "italic" : "normal"}>
                                {value || "--"}
                            </Typography>
                        ) : link ? (
                            <Link href={link} target="_blank" rel="noopener noreferrer">
                                <Typography variant="body2" fontStyle={featureDeleted ? "italic" : "normal"}>
                                    {link}
                                </Typography>
                            </Link>
                        ) : (
                            <></>
                        )}
                    </Box>
                ))}
            </Box>

            <Popover
                open={flightPopoverOpen}
                anchorEl={flightPopoverAnchorEl}
                onClose={() => setFlightPopoverAnchorEl(null)}
                anchorOrigin={{ vertical: "bottom", horizontal: "center" }}
                transformOrigin={{ vertical: "top", horizontal: "center" }}
                slotProps={{ paper: { sx: { px: 2, py: 1 } } }}
                container={mapContainer}
            >
                <FormGroup>
                    {[{ name: "None", flight_uuid: "N/A" }, ...publishedOperations].map((operation, index) => (
                        <FormControlLabel
                            key={index}
                            label={operation.name}
                            control={
                                <Checkbox
                                    size="small"
                                    checked={getAssociatedFlightButtonCheckedFromFeatureAndFlightUuid(featuresClicked[0], operation.flight_uuid)}
                                    onChange={() => handleAssociatedFlightButtonClick(operation.flight_uuid)}
                                />
                            }
                        />
                    ))}
                </FormGroup>
            </Popover>

            <Popover
                open={zcPopoverOpen}
                anchorEl={zcPopoverAnchorEl}
                onClose={() => setZcPopoverAnchorEl(null)}
                anchorOrigin={{ vertical: "bottom", horizontal: "center" }}
                transformOrigin={{ vertical: "top", horizontal: "center" }}
                slotProps={{ paper: { sx: { px: 2, py: 1 } } }}
                container={mapContainer}
            >
                <FormGroup>
                    {[{ name: "None", config_uuid: "" }, ...allZcConfigurations].map((config, index) => (
                        <FormControlLabel
                            key={index}
                            label={config.name}
                            control={
                                <Checkbox
                                    size="small"
                                    checked={getZcButtonCheckedFromFeatureAndZcConfigurations(featuresClicked[0], user, config.config_uuid)}
                                    onChange={() => handleZcConfigurationButtonClick(config.config_uuid)}
                                />
                            }
                        />
                    ))}
                </FormGroup>
            </Popover>
        </Paper>
    ) : (
        <></>
    );
}
